import { useCallback } from 'react';
import { useAnalytics } from '@/services/analytics';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { isAnonymousMergeEvent } from './activity-log-types';
import type { ActivityLog, ActivityLogRow, ActivityLogTab } from './types';

/** The `Activity Tab` property value shared by all three page events. */
export function activityTabName(tab: ActivityLogTab): 'Identified' | 'Anonymous' {
  return tab === 'anonymous' ? 'Anonymous' : 'Identified';
}

/**
 * Wire value for a profile-merge **source**. Note the space: this is
 * `ActivityLogSourceType.ProfileMerge` from Ember (`ui/app/utils/activity-log-source-types.ts`),
 * not the similarly-named `LOG_TYPE.ProfileMerge` log type (`'profile_merge'`, underscore).
 */
const PROFILE_MERGE_SOURCE = 'profile merge';

/**
 * The log records an activity that came from merging two people — i.e. one of its *sources* is a
 * profile merge, which is not the same population as logs whose own type is `LOG_TYPE.ProfileMerge`
 * (those are the merge itself). Ported from Ember's `LogModel.isPeopleMergeEvent`
 * (`ui/app/models/log.ts`), which is the definition behind the historical `Merge Activity Type`
 * series — so switching this to the log type would silently break continuity with it.
 */
function isPeopleMergeEvent(log: ActivityLog): boolean {
  return (log.sources ?? []).some((source) => source.type === PROFILE_MERGE_SOURCE);
}

/** Properties for `Log: Activity Log Specific Activity Viewed` (a row expansion). */
export function specificActivityViewedProps(tab: ActivityLogTab, log: ActivityLog) {
  return {
    'Activity Tab': activityTabName(tab),
    'Merged Anonymous Event': isAnonymousMergeEvent(log),
    'Merge Activity Type': isPeopleMergeEvent(log),
  };
}

/**
 * The page's three analytics events, ported from the Ember logs page (FPS-2304): a page view per
 * tab, plus callbacks for the page to fire on "Load more" and on row expansion.
 */
export function useActivityLogsAnalytics(tab: ActivityLogTab, rows: ActivityLogRow[]) {
  const { track } = useAnalytics();
  const workspace = useCurrentWorkspace();
  const tabName = activityTabName(tab);

  usePageViewTracking(
    useCallback(() => {
      track('logs', 'viewed', { 'Activity Tab': tabName });
    }, [track, tabName]),
    // `track` drops the event outright while the workspace context is unresolved, and a page view
    // is only ever attempted once per location — so gate on the very condition `track` checks.
    // Gating on the route's workspace id instead isn't equivalent: for a numeric route param that
    // id is available immediately, so a cold load would mark the view tracked before the context
    // arrived and lose it for good.
    workspace != null,
    // The tab is a path segment but the filters are search params, so keying on the pathname alone
    // makes a tab switch a new page view while a filter change isn't one.
    { ignoreSearch: true },
  );

  const trackMoreLogsLoaded = useCallback(() => {
    track('logs', 'moreLogsLoaded', { 'Activity Tab': tabName });
  }, [track, tabName]);

  const trackRowsExpanded = useCallback(
    (rowIds: string[]) => {
      for (const id of rowIds) {
        const row = rows.find((candidate) => candidate.log.id === id);
        if (row != null) track('logs', 'specificActivityViewed', specificActivityViewedProps(tab, row.log));
      }
    },
    [rows, track, tab],
  );

  return { trackMoreLogsLoaded, trackRowsExpanded };
}
